import moment from "moment"
import { mapValues, some, isEqual } from "lodash"

const INTRADAY_CONTRIBUTION_PREFIX_REGEX = /^intradayContribution(?:1d|5d|1m|Mtd)(.*)$/
export const INTRADAY_CONTRIBUTION_AUTO_INVALIDATION_MAX_DAYS = 31

export function getInstrumentSuffixForIntradayContributionPrefix(prefix = "") {
  const match = INTRADAY_CONTRIBUTION_PREFIX_REGEX.exec(prefix)
  return match ? match[1] : null
}

export function normalizeContributorKey(rowId) {
  return String(rowId || "").replace(/\./g, "_")
}

function addSuffixForModelProviderId(suffixesByModelProviderId, modelProviderId, suffix) {
  if (!suffixesByModelProviderId.has(modelProviderId)) {
    suffixesByModelProviderId.set(modelProviderId, new Set())
  }
  suffixesByModelProviderId.get(modelProviderId).add(suffix)
}

export function shouldAutoInvalidateIntradayContributionGathererOnPricingUpdate(gatherer) {
  if (gatherer?.shouldAutoInvalidateOnPricingUpdates === false) {
    return false
  }

  const from = gatherer?.variables?.from
  const to = gatherer?.variables?.to
  if (!from || !to) {
    return true
  }

  const fromMoment = moment(from)
  const toMoment = moment(to)
  if (!fromMoment.isValid() || !toMoment.isValid()) {
    return false
  }

  return Math.abs(toMoment.diff(fromMoment, "days")) <= INTRADAY_CONTRIBUTION_AUTO_INVALIDATION_MAX_DAYS
}

export function getIntradayContributionAutoRefreshWarningState({
  hasIntradayContributionGatherers,
  shouldAutoRefreshIntradayContributionOnPricingUpdates,
  hasShownWarning,
}) {
  if (!hasIntradayContributionGatherers || shouldAutoRefreshIntradayContributionOnPricingUpdates) {
    return { shouldShowWarning: false, nextHasShownWarning: false }
  }

  if (hasShownWarning) {
    return { shouldShowWarning: false, nextHasShownWarning: true }
  }

  return { shouldShowWarning: true, nextHasShownWarning: true }
}

export function shouldSubscribeToIntradayPriceTicks({
  intradayPriceGatherers,
  intradayContributionQueryGatherers,
}) {
  return intradayPriceGatherers.length > 0 || intradayContributionQueryGatherers.length > 0
}

/**
 * Builds an index from modelProviderId -> contribution metric suffixes present in the current tree.
 * Used to invalidate only the affected intraday contribution gatherers after price ticks.
 */
export function buildContributionSuffixIndexByModelProviderId(root) {
  const suffixesByModelProviderId = new Map()

  const nodesToVisit = [root]
  while (nodesToVisit.length > 0) {
    const currentNode = nodesToVisit.pop()
    if (!currentNode || typeof currentNode !== "object") {
      continue
    }

    for (const [prefix, contributionValue] of Object.entries(currentNode.values || {})) {
      const suffix = getInstrumentSuffixForIntradayContributionPrefix(prefix)
      const modelProviderId = contributionValue?.descriptors?.modelProviderId
      if (suffix == null || !modelProviderId) {
        continue
      }

      addSuffixForModelProviderId(suffixesByModelProviderId, modelProviderId, suffix)
      addSuffixForModelProviderId(suffixesByModelProviderId, normalizeContributorKey(modelProviderId), suffix)
    }

    if (!currentNode.children || typeof currentNode.children !== "object") {
      continue
    }
    nodesToVisit.push(...Object.values(currentNode.children))
  }

  return suffixesByModelProviderId
}

function mergeContributorNodeRecursive(mergedNode, sourceNode) {
  if (!mergedNode) {
    mergedNode = {
      label: sourceNode.label,
      values: {},
    }
  }

  for (const category in sourceNode.values) {
    mergedNode.values[category] = sourceNode.values[category]
  }

  // Sometimes a contributor shows up as notHeld in the composition query, but is held in the performance query
  // We want to show these securities as no longer held in the application
  // This is the case for securities that only have a pnl value, and no economic exposure
  if (mergedNode.notHeld && sourceNode.isNoLongerHeld) {
    mergedNode.isNoLongerHeld = sourceNode.isNoLongerHeld
  }

  if (sourceNode.children) {
    if (!mergedNode.children) {
      mergedNode.children = {}
    }

    for (const childId in sourceNode.children) {
      const child = sourceNode.children[childId]
      if (!mergedNode.children[childId]) {
        mergedNode.children[childId] = {
          id: child.id,
          isFactor: child.isFactor,
          label: child.label,
          notHeld: child.notHeld,
          isUnmodeled: child.isUnmodeled,
          isNoLongerHeld: child.isNoLongerHeld,
          values: {},
          pinnedRowOrder: child.pinnedRowOrder,
          tooltipValue: child.tooltipValue,
        }
      }
      mergeContributorNodeRecursive(mergedNode.children[childId], child)
    }
  }

  return mergedNode
}

export function mergeContributorTrees(contributorTrees) {
  return contributorTrees.reduce((mergedTree, sourceTree) => mergeContributorNodeRecursive(mergedTree, sourceTree), null)
}

function mergeFlatContributorsIntoRow(row, flatContributors, rowIdForKey) {
  if (!row) return row
  const lookupId = rowIdForKey ?? row?.id

  let values = row.values
  for (const { prefix, valuesByRowId } of flatContributors) {
    const next = lookupId == null ? undefined : valuesByRowId?.[lookupId]
    if (!next) continue
    const prev = values?.[prefix]
    const contributorDidChange = !isEqual(prev, next)
    if (!contributorDidChange) continue
    if (values === row.values) values = { ...(row.values || {}) }
    values[prefix] = next
  }

  let children = row.children
  if (row.children) {
    const newChildren = mapValues(row.children, (child, childKey) =>
      mergeFlatContributorsIntoRow(child, flatContributors, childKey),
    )
    if (some(newChildren, (child, key) => child !== row.children[key])) children = newChildren
  }

  if (values === row.values && children === row.children) return row
  return { ...row, values, children }
}

export function mergeFlatContributors(data, flatContributors) {
  if (!data || !flatContributors?.length) return data
  return mergeFlatContributorsIntoRow(data, flatContributors)
}
